import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";

import { periodWindowLabelTitle } from "../../helpers/periodWindowLabel";
import { resolveCardWindow } from "../../helpers/cardWindow";
import useProfitTrend, {
  DEFAULT_LINES,
  goalLinePerDay,
  goalOf,
  goalStatus,
  laborBaseOf,
  money,
  pct,
  resolveLaborWidget,
} from "./data";
import TrendChart, { hasLossIn, SERIES, seriesColorsOf } from "./TrendChart";
import CardBusy from "../CardScope/CardBusy";
import "./profit.css";

/**
 * Revenue, Expenses and Labor over time - and what's left.
 *
 * The card answers one question the payroll table beside it cannot: not "what
 * does each department cost right now" but "which way is this going, and are
 * we about to cross a line we set". So the three lines carry the shape, the
 * tiles carry the four shares that must add to 100%, and every tile can be
 * given a ceiling or a floor that turns it amber then red on its own.
 *
 * Labor is not re-derived here. It is READ from a Payroll vs Revenue card on
 * the same board - whichever one you point this at - so the two cards can
 * never quietly disagree about what payroll cost. Every figure is computed in
 * ./data.js; nothing in this file decides arithmetic.
 */

/* The gate palette the rest of the board already uses, borders included - a
   Gate card tints its frame by status and these tiles do the same, so the two
   read as siblings (GATE_STYLE in components/BeatTheYeti/index.jsx). */
const TONES = {
  ok: { ink: "#12a150", soft: "rgba(18, 161, 80, 0.1)", border: "rgba(18, 161, 80, 0.4)" },
  warn: { ink: "#f79009", soft: "rgba(247, 144, 9, 0.1)", border: "rgba(247, 144, 9, 0.4)" },
  bad: { ink: "#d92d20", soft: "rgba(240, 68, 56, 0.1)", border: "rgba(240, 68, 56, 0.4)" },
  flat: { ink: null, soft: null, border: null },
};

const TILES = [
  { id: "revenue" },
  { id: "net" },
  { id: "expenses" },
  { id: "labor" },
];

const IcBack = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);

/**
 * What a click on a Labor point opens into: that one day, broken down by the
 * departments that made it up. `laborShare` (data.js) is each department's
 * cut of THAT DAY's labor, not of revenue — the number this view is actually
 * about — so the bars here answer a different question than the tooltip's
 * own department rows do.
 */
const LaborDrilldown = ({ row, name, color, onBack }) => {
  const depts = (row.depts || []).filter((d) => d.cost > 0).sort((a, b) => b.cost - a.cost);
  return (
    <div className="pft-drill">
      <div className="pft-drill-head">
        <button type="button" className="pft-drill-back" onClick={onBack}>
          {IcBack} Back to trend
        </button>
        <div className="pft-drill-title">
          <span className="pft-drill-day">{row.full || row.label}</span>
          <span className="pft-drill-total">{name} <b>{money(row.labor)}</b></span>
        </div>
      </div>
      {depts.length === 0 ? (
        <div className="pft-empty">
          <b>No {name.toLowerCase()} this day</b>
          Nothing was logged against any department for {row.full || row.label}.
        </div>
      ) : (
        <div className="pft-drill-list">
          {depts.map((d) => (
            <div className="pft-drill-row" key={d.key}>
              <div className="pft-drill-rowh">
                <span>{d.label}</span>
                <b>{money(d.cost)}</b>
              </div>
              <div className="pft-drill-bar">
                <span
                  style={{
                    width: `${Math.min(100, Math.max(0, d.laborShare ?? 0))}%`,
                    background: color,
                  }}
                />
              </div>
              <span className="pft-drill-pct">{pct(d.laborShare)} of {name.toLowerCase()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
LaborDrilldown.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  row: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
};

/* Pulled out of the JSX below to dodge a nested ternary (blocked / drilled-in
   / normal) — three real states read better as early returns than as one
   expression. */
function body({
  blocked, drillRow, data, unit, labels, colors, shown, shows, settings, goalLines, legend, nameOf, setDrillKey,
  laborBase, showPace, showLoss,
}) {
  if (blocked) {
    /* Loading has its own card-level veil (CardBusy) — nothing to say here. */
    if (blocked.loading) return null;
    return (
      <div className="pft-empty">
        <b>{blocked.head}</b>
        {blocked.body}
      </div>
    );
  }
  if (drillRow) {
    return <LaborDrilldown row={drillRow} name={nameOf("labor")} color={colors.labor} onBack={() => setDrillKey(null)} />;
  }
  return (
    <>
      <div className="pft-chart">
        <TrendChart
          data={data.series}
          unit={unit}
          labels={labels}
          colors={colors}
          shown={shown}
          showDepts={shows("depts") && data.departments.length > 0}
          laborBase={laborBase}
          showPace={showPace}
          showLoss={showLoss}
          curve={settings?.curve}
          grid={shows("grid")}
          dots={shows("dots")}
          goalLines={goalLines}
          onLaborPointClick={
            shown.labor && shows("depts") && data.departments.length > 0
              ? (row) => setDrillKey(row.key)
              : null
          }
        />
      </div>
      {shows("legend") && (
        <div className="pft-legend">
          {legend.map((l) => (
            <span className="pft-legend-i" key={l.id}>
              <i style={{ background: colors[l.id] }} />
              {nameOf(l.id)}
            </span>
          ))}
          {/* Only named when there is one on the chart to point at - a legend
              key for an invisible thing is a question, not an answer. */}
          {showLoss && shown.revenue && unit !== "%" && hasLossIn(data.series, unit) && (
            <span className="pft-legend-i">
              <i className="pft-legend-band" style={{ background: colors.loss }} />
              {nameOf("loss")}
            </span>
          )}
          {showPace && (
            <span className="pft-legend-i">
              <i className="pft-legend-dash" style={{ background: colors.pace }} />
              {/* The goal, not the daily figure the line is drawn at: the
                  number to recognise is the one that was typed into Setup.
                  The hover still gives the day's own figure. */}
              {nameOf("pace")} {money(data.paceGoal)}
              {data.periodWord ? `/${data.periodWord}` : ""}
            </span>
          )}
          <span className="pft-legend-grain">
            {{ day: "by day", week: "by week", month: "by month" }[data.grain]}
            {/* The count the whole pace rests on, said out loud rather than
                left implied by a dashed line. */}
            {data.workingDays > 0 ? ` · ${data.workingDays} working days` : ""}
            {unit === "%" ? " · % of revenue" : ""}
            {/* Said on the card, not just in Setup: the Labor line is the one
                thing here that isn't measured from the axis, and a reader who
                doesn't know that will read it as a much bigger bill. */}
            {laborBase !== "zero" && shown.labor
              ? ` · ${nameOf("labor")} from ${nameOf("expenses")}`
              : ""}
          </span>
        </div>
      )}
    </>
  );
}

/* `paced` is a money goal being judged part-way through its period, where the
   verdict is about the RUN RATE, not the finished total - so it says "Behind"
   and "On pace" rather than "Under" and "On track", which would read as a
   final score on the 3rd of the month. */
const statusWord = (status, dir, paced) => {
  if (status === "bad") {
    if (dir !== "min") return "Over";
    return paced ? "Behind" : "Under";
  }
  if (status === "warn") return "Close";
  if (dir !== "min") return "Under";
  return paced ? "On pace" : "On track";
};

/**
 * A goal's own figure, written the way it was typed - plus, for a money goal
 * part-way through its period, the figure the tile is ACTUALLY judging. A tile
 * that shows "$250,000" and says "Behind" while holding $172,732 looks like it
 * cannot do arithmetic; "$204,545 by now" is the number that makes it true.
 */
const goalText = (goal, elapsed) => {
  if (!goal.on || goal.limit === "" || goal.limit == null) return null;
  const onShare = goal.unit === "%";
  const limit = onShare ? pct(Number(goal.limit)) : money(goal.limit);
  const base = `${goal.dir === "min" ? "at least" : "max"} ${limit}`;
  if (onShare || !(elapsed > 0) || elapsed >= 1) return base;
  return `${base} · ${money(Number(goal.limit) * elapsed)} by now`;
};

const ProfitTrend = ({
  settings, title, boardWindow, boardCustom, boardWidgets,
}) => {
  /* "Match the dashboard" (default) or a range pinned in Setup - the same rule
     every other cross-workspace card follows (helpers/cardWindow.js), so this
     card doesn't grow a second, different meaning for "date range". */
  const { window: bucket, custom: resolvedCustom } = resolveCardWindow(settings, {
    window: boardWindow,
    custom: boardCustom,
  });
  const custom = bucket === "custom" ? (resolvedCustom || boardCustom) : null;

  /* Deliberately not memoised: it's one filter over a handful of cards, and
     any cheap cache key (ids, comps) would miss the case that matters most -
     the payroll card's OWN settings being edited, which changes nothing about
     its id but everything about the figure read from it. */
  const link = resolveLaborWidget(settings, boardWidgets);
  const laborMode = settings?.laborMode === "manual" ? "manual" : "widget";
  const laborOptions = laborMode === "widget" ? (link.widget?.options || null) : null;

  const data = useProfitTrend(settings, laborOptions, bucket, custom);

  const hide = settings?.hide || {};
  const shows = (key) => !hide[key];
  const labels = settings?.labels || {};
  const nameOf = (id) => labels[id] || SERIES[id].label;
  const colors = seriesColorsOf(settings);
  const unit = settings?.unit === "%" ? "%" : "$";
  /* Expenses is Labor's zero unless this card says otherwise - the whole
     Labor line rides up and down on it (see laborBaseOf in ./data.js). */
  const laborBase = laborBaseOf(settings);
  /* The pace line needs three things to be true: a Revenue goal in dollars to
     divide (data.js works that out), working days to divide it by, and the
     card not to have switched it off under Appearance. */
  const showPace = shows("pace") && data.dailyPace != null;
  /* The red patch where a day's revenue came in under what that day cost. */
  const showLoss = shows("loss");
  const cardTitle = title || settings?.title || "Revenue, Expenses & Labor";
  /* "This month" is what every other card puts here, and it is the one thing
     this header can't say on its own any more: the card now draws the WHOLE
     month while the figures stop at today, so the dates have to be on the
     card. `data.displayTo` is the end the chart reaches, not the end the
     totals were added up to. */
  const periodLabel = data.displayTo && bucket === "month"
    ? `${moment(data.from).format("MMM D")} – ${moment(data.displayTo).format("D")}`
    : periodWindowLabelTitle(bucket, custom);

  /* Clicking a point on the Labor line freezes the card on that one day and
     swaps the trend chart for that day's department breakdown - the question
     a point on Labor actually raises ("who made up THIS day's bill") that a
     hover alone can't answer at a glance. Keyed by bucket key, not the row
     itself, so a data refresh under an open drill-in still resolves to the
     same day instead of going stale. Reset whenever the card moves to a
     different window - a drill-in from last week's chart has nothing to
     point at once the range has moved on. */
  const [drillKey, setDrillKey] = useState(null);
  const customKey = custom ? `${custom.from || ""}~${custom.to || ""}` : "";
  useEffect(() => setDrillKey(null), [bucket, customKey]);
  const drillRow = drillKey ? data.series.find((s) => s.key === drillKey) : null;

  const figures = {
    revenue: { amount: data.revenue, share: data.revenuePct },
    expenses: { amount: data.expenses, share: data.expensesPct },
    labor: { amount: data.labor, share: data.laborPct },
    net: { amount: data.net, share: data.netPct },
  };

  const tiles = TILES.filter((t) => shows(t.id)).map((t) => {
    const goal = goalOf(settings, t.id);
    const status = goalStatus(goal, figures[t.id], data.paceElapsed);
    return {
      ...t,
      goal,
      status,
      tone: TONES[status || "flat"],
      name: nameOf(t.id),
      amount: figures[t.id].amount,
      share: figures[t.id].share,
    };
  });

  /* Only the goals the chart can honestly draw.
     A "% of revenue" limit is already a per-bucket figure - every point has
     its own share - so on a percent axis it goes straight across.
     A DOLLAR limit does not. It is a figure for the whole period, and the
     chart plots one day at a time: drawing "$250,000" flat across a chart
     whose biggest day is $22k doesn't just mislead, it drags the axis up to
     $260k (ifOverflow="extendDomain") and flattens every real line into the
     floor. So a dollar goal is divided by the period's working days first and
     drawn as what ONE DAY has to do - the same arithmetic the pace line runs,
     which is the only reading of a monthly goal a daily chart can carry.
     Where the buckets are weeks or months that share isn't constant, so
     nothing is drawn rather than a figure that is right for no bucket; the
     tile still reports the goal and its status either way. */
  const goalLines = shows("goalLines")
    ? TILES.map(({ id }) => {
      const goal = goalOf(settings, id);
      if (!goal.on) return null;
      const word = goal.dir === "min" ? "floor" : "cap";
      if (unit === "%") {
        if (goal.unit !== "%" || goal.limit === "" || goal.limit == null) return null;
        return { id, value: Number(goal.limit), label: `${nameOf(id)} ${word}` };
      }
      /* The revenue goal per working day IS the pace line, already drawn and
         already labelled - a second identical line under a second name is
         just two things to read. */
      if (id === "revenue" && showPace) return null;
      const value = goalLinePerDay(goal, data.revenue, data.grain, data.workingDays);
      if (value == null) return null;
      return { id, value, label: `${nameOf(id)} ${word}/day` };
    }).filter(Boolean)
    : [];

  /* Which series are PLOTTED, kept apart from which have a tile: the sketch
     draws three lines but reads four figures, and an owner who wants Net
     profit charted shouldn't have to give up its tile to get it. */
  const lines = Array.isArray(settings?.lines) ? settings.lines : DEFAULT_LINES;
  const shown = {
    revenue: lines.includes("revenue"),
    expenses: lines.includes("expenses"),
    labor: lines.includes("labor"),
    net: lines.includes("net"),
  };

  const legend = [
    ...(unit === "%" ? [] : [{ id: "revenue", on: shown.revenue }]),
    { id: "expenses", on: shown.expenses },
    { id: "labor", on: shown.labor },
    { id: "net", on: shown.net || (unit === "%" && shown.revenue) },
  ].filter((l) => l.on);

  /* -- What's missing, said plainly ---------------------------------------
     Each of these is a real dead end, not a slow load: the card would draw a
     confident flat line at zero and look like a finding. */
  const notReady = () => {
    if (data.loading) return { loading: true };
    if (data.revenue <= 0) {
      return {
        head: "No revenue yet",
        body: "Every share on this card is measured against revenue for the period. Add a Total Revenue metric, point this card at one, or type the figure in Edit component → Setup.",
      };
    }
    if (laborMode === "widget" && link.missing) {
      return {
        head: "No Payroll vs Revenue card on this dashboard",
        body: "Labor is read from one, so this card needs one on the board — add it, or switch Labor to a typed figure in Edit component → Setup.",
      };
    }
    if (laborMode === "widget" && link.ambiguous) {
      return {
        head: link.stale ? "That payroll card is gone" : `${link.choices.length} Payroll vs Revenue cards on this dashboard`,
        body: link.stale
          ? "The card this one was reading has been removed or replaced. Pick another in Edit component → Setup."
          : "They track different departments, so this card won't guess between them. Pick which one feeds Labor in Edit component → Setup.",
      };
    }
    if (!data.series.length) {
      return { head: "Nothing in this range", body: "Widen the date range in Edit component." };
    }
    return null;
  };

  const blocked = notReady();

  return (
    <div className={`gmbw-sales-card pft${data.refreshing ? " pft-refreshing" : ""}`}>
      <CardBusy active={data.loading || data.refreshing} cover={data.loading} label={data.loading ? "Loading…" : "Updating for date range…"} />
      <div className="gmbw-sales-head">
        <span className="gmbw-sales-title">{cardTitle}</span>
        <span className="gmbw-sales-period">{periodLabel}</span>
      </div>
      <div className="gmbw-sales-body">
        {/* Tiles are suppressed while something is missing: with no revenue
            they would all read $0 / 0%, which is a statement, not a blank. */}
        {shows("tiles") && tiles.length > 0 && !blocked && (
          /* The tile count drives the grid's column count once the card is
             wide enough for one row (profit.css) — tiles are configurable, so
             CSS cannot know it on its own. */
          <div className="pft-sum" style={{ "--pft-n": String(tiles.length) }}>
            {tiles.map((t) => {
              const goalLabel = goalText(t.goal, data.paceElapsed);
              /* A money goal mid-period is judged on its run rate, which is
                 what the pill's wording has to say. */
              const paced = t.goal.unit !== "%" && data.paceElapsed < 1;
              return (
                <div
                  className="pft-sum-i"
                  key={t.id}
                  style={{
                    "--pft-accent": colors[t.id],
                    ...(t.tone.border ? { borderColor: t.tone.border } : null),
                  }}
                >
                  <span className="pft-k">
                    <i className="pft-swatch" style={{ background: colors[t.id] }} />
                    {t.name}
                    {t.id === "revenue" && !data.revenueLive && <small> typed in</small>}
                  </span>
                  <span className="pft-sum-v">
                    {/* The share leads on every tile but Revenue - the sketch's
                        own hierarchy, and the number a goal is actually set
                        against. Revenue has no share worth printing (it is
                        always 100% of itself), so it leads with the money. */}
                    <b style={t.tone.ink ? { color: t.tone.ink } : undefined}>
                      {t.id === "revenue" ? money(t.amount) : pct(t.share)}
                    </b>
                    {t.status && (
                      <span className="pft-pill" style={{ color: t.tone.ink, background: t.tone.soft }}>
                        {statusWord(t.status, t.goal.dir, paced)}
                      </span>
                    )}
                  </span>
                  <span className="pft-sub">
                    {/* Revenue's own figure is already the headline above, so
                        its sub-line carries the period instead - unless a goal
                        needs the room, in which case the card header is
                        saying the period anyway. */}
                    {t.id === "revenue" && goalLabel ? "" : `${t.id === "revenue" ? periodLabel : money(t.amount)}`}
                    {goalLabel ? `${t.id === "revenue" ? "" : " · "}${goalLabel}` : ""}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {body({
          blocked,
          drillRow,
          data,
          unit,
          labels,
          colors,
          shown,
          shows,
          settings,
          goalLines,
          legend,
          nameOf,
          setDrillKey,
          laborBase,
          showPace,
          showLoss,
        })}
      </div>
    </div>
  );
};

ProfitTrend.propTypes = {
  /* The card's own settings blob - the same free-form shape every widget stores. */
  // eslint-disable-next-line react/forbid-prop-types
  settings: PropTypes.object,
  title: PropTypes.string,
  boardWindow: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  boardCustom: PropTypes.object,
  /* Every other card on this board, so Labor can be read from a Payroll vs
     Revenue one sitting beside it. Empty where a board doesn't thread it
     (a thumbnail preview) - the card then asks for a typed figure instead. */
  // eslint-disable-next-line react/forbid-prop-types
  boardWidgets: PropTypes.array,
};

ProfitTrend.defaultProps = {
  settings: {},
  title: undefined,
  boardWindow: null,
  boardCustom: null,
  boardWidgets: [],
};

export default ProfitTrend;
